import DailyActivity from './DailyActivity'
import DocumentLink from './documents/DocumentLink'
import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { apiFetch, ApiError } from '../lib/api'
import { failureLabel } from '../lib/documents'
import {
  humanizeToken,
  humanizeTokensIn,
  cleanEngineText,
  formatOfficerDate,
  formatOfficerDateTime,
} from '../lib/officerFormat'
import { useAuth } from '../contexts/useAuth'
import { useAuthSession } from '../lib/authSession'
import { HelpDot } from './guidance/Guidance'
import { LinkedSentence } from './Sentence'
import { EmptyState, ErrorState, Skeleton } from './state'
import FundSelector from './FundSelector'
import { useFundScope, rememberFundChoice } from './fundScope'
import {
  fetchDocuments,
  fetchOrphans,
  PROCESSING,
  NEEDS_ATTENTION,
  documentNeedsAttention,
  documentReceivedSince,
  type DocRow,
  type OrphanRow,
} from './documentsList'
import {
  batchUploadOutcomes,
  type BatchUploadFileResult,
  type FileOutcome,
} from './documents/uploadOutcomes'
import './documents.css'

const STATUS_LABELS: Record<string, string> = {
  processed: 'Processed',
  uploaded: 'Processing',
  processing: 'Processing',
  split: 'Processing',
  failed: 'Failed',
  unprocessable: 'Could not be read',
  needs_reclassify: 'Needs reclassification',
  superseded: 'Superseded',
}
function parkedReason(reason: string): string {
  const cleaned = reason.replace(/_/g, ' ')
  return cleaned.includes('derivable') ? 'no fund could be matched yet' : cleaned
}
export default function DocumentsPage() {
  const authSession = useAuthSession()
  return <ScopedDocumentsPage key={authSession ?? ''} />
}

function ScopedDocumentsPage() {
  const { user } = useAuth()
  const canUpload = user !== null
  const [params, setParams] = useSearchParams()
  const { fundId } = useFundScope()
  const location = useLocation()
  const view = params.get('view') === 'history' ? 'history' : 'documents'
  const query = params.get('q') ?? ''
  const kind = params.get('kind') ?? ''
  const status = params.get('status') ?? 'all'
  const receivedSince = params.get('received_since')
  const received = receivedSince ? 'since' : (params.get('received') ?? 'any')
  const objectParam = params.get('object')
  const [docs, setDocs] = useState<DocRow[] | null>(null)
  const [orphans, setOrphans] = useState<OrphanRow[]>([])
  const [funds, setFunds] = useState<{ id: string; name: string }[] | null>(null)
  const [fundError, setFundError] = useState<unknown>(null)
  const [error, setError] = useState<unknown>(null)
  const [tick, setTick] = useState(0)
  const [outcomes, setOutcomes] = useState<FileOutcome[]>([])
  const [busy, setBusy] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const [objectState, setObjectState] = useState<{
    key: string
    title?: string
    docs?: Set<string>
    error?: unknown
  } | null>(null)
  const objectKey = `${fundId}:${objectParam}`
  const load = useCallback(() => setTick((t) => t + 1), [])
  // Browser history updates before React commits the next render. Base rapid
  // successive edits on that current URL so typing after Clear cannot restore
  // a filter from an older rendered search-param snapshot.
  const changeFilters = useCallback(
    (change: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(window.location.search)
      change(next)
      setParams(next, { replace: true })
    },
    [setParams]
  )
  const setFilter = (key: string, value: string) =>
    changeFilters((next) => {
      if (value) next.set(key, value)
      else next.delete(key)
      if (key === 'status' && value === 'unmatched') {
        next.set('fund', '')
        next.delete('object')
      }
    })
  const chooseFund = (id: string | null) => {
    rememberFundChoice(id)
    changeFilters((next) => {
      if (id) next.set('fund', id)
      else next.delete('fund')
      if (id && next.get('status') === 'unmatched') next.delete('status')
      next.delete('object')
    })
  }
  // An unmatched document cannot satisfy a fund/object filter. Normalize
  // shared links too, so the remembered fund cannot hide this explicit view.
  useEffect(() => {
    if (status !== 'unmatched' || (!fundId && !objectParam && params.has('fund'))) return
    changeFilters((next) => {
      if (next.get('status') !== 'unmatched') return
      next.set('fund', '')
      next.delete('object')
    })
  }, [status, fundId, objectParam, params, changeFilters])

  useEffect(() => {
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    const pull = async (background = false) => {
      try {
        const [rows, parked] = await Promise.all([
          fetchDocuments({ signal: controller.signal, background }),
          fetchOrphans({ signal: controller.signal, background }),
        ])
        if (controller.signal.aborted) return
        setDocs(rows)
        setOrphans(parked)
        setError(null)
        if (rows.some((d) => PROCESSING.has(d.status)))
          timer = setTimeout(() => void pull(true), 15000)
      } catch (e) {
        if (!controller.signal.aborted) setError(e)
      }
    }
    void pull()
    setFundError(null)
    apiFetch<{ id: string; name: string }[]>('/api/funds', { signal: controller.signal })
      .then((rows) => {
        if (!controller.signal.aborted) setFunds(rows)
      })
      .catch((e) => {
        if (!controller.signal.aborted) setFundError(e)
      })
    return () => {
      controller.abort()
      clearTimeout(timer)
    }
  }, [tick])
  useEffect(() => {
    if (!objectParam) return
    const match = /^(entity|instrument|position|trade):(.+)$/.exec(objectParam)
    if (!match || !fundId) {
      setObjectState({
        key: objectKey,
        error: new Error(
          'This object link is incomplete. Clear the object filter to see documents.'
        ),
      })
      return
    }
    const controller = new AbortController()
    apiFetch<{ title: string; evidence: { document_id: string }[] }>(
      `/api/funds/${encodeURIComponent(fundId)}/objects/${match[1]}/${encodeURIComponent(match[2])}`,
      { signal: controller.signal }
    )
      .then((result) => {
        if (!controller.signal.aborted)
          setObjectState({
            key: objectKey,
            title: result.title,
            docs: new Set(result.evidence.map((e) => e.document_id)),
          })
      })
      .catch((e) => {
        if (!controller.signal.aborted) setObjectState({ key: objectKey, error: e })
      })
    return () => controller.abort()
  }, [objectParam, fundId, objectKey, tick])
  async function uploadFiles(files: File[]) {
    if (!canUpload || busy || files.length === 0) return
    setBusy(true)
    setOutcomes([])
    try {
      if (files.length === 1) {
        const fd = new FormData()
        fd.append('file', files[0])
        // apiFetch handles auth + central 401-redirect; it skips the JSON
        // Content-Type for FormData so the browser sets the multipart
        // boundary. A non-2xx response throws ApiError → outer catch.
        const body = await apiFetch<{
          intake_status?: string
          message?: string
          reason?: string
          detail?: string
        }>('/api/documents/upload', { method: 'POST', body: fd })
        setOutcomes([
          {
            name: files[0].name,
            ok: (body.intake_status ?? 'validated') === 'validated',
            detail: body.message ?? body.reason ?? body.detail ?? 'in the pipeline',
          },
        ])
      } else {
        const fd = new FormData()
        files.forEach((f) => fd.append('files', f))
        const body = await apiFetch<{
          results?: BatchUploadFileResult[]
        }>('/api/documents/upload/batch', { method: 'POST', body: fd })
        setOutcomes(batchUploadOutcomes(body.results ?? []))
      }
    } catch (e) {
      // A 401 has already redirected to /login via apiFetch. For other
      // failures, prefer the server-supplied reason from the ApiError body.
      let detail = 'upload failed'
      if (e instanceof ApiError) {
        const b = e.body as { detail?: string; reason?: string; message?: string } | undefined
        detail = b?.detail ?? b?.reason ?? b?.message ?? `upload failed (${e.status})`
      } else if (e instanceof Error) {
        detail = e.message
      }
      setOutcomes([
        {
          name: files.map((f) => f.name).join(', '),
          ok: false,
          detail,
        },
      ])
    } finally {
      setBusy(false)
      load()
    }
  }

  const parkedIds = new Set(orphans.map((o) => o.document_id))
  const rows = docs ?? []
  const selectorFunds = (funds ?? []).map((f) => ({
    id: f.id,
    name: f.name,
    open: rows.filter((d) => d.fund_ids?.includes(f.id)).length,
  }))
  const scoped = rows.filter((d) => !fundId || d.fund_ids?.includes(fundId))
  const currentObject = objectState?.key === objectKey ? objectState : null
  const objectLoading = !!objectParam && !currentObject
  const currentError = error || (objectParam && currentObject?.error)
  const types = [
    ...new Set(rows.map((d) => d.document_type).filter((v): v is string => !!v)),
  ].sort()
  const threshold = new Date()
  threshold.setHours(0, 0, 0, 0)
  if (received === '7' || received === '30')
    threshold.setDate(threshold.getDate() - Number(received) + 1)
  const visible = scoped.filter((d) => {
    if (objectParam && (!currentObject?.docs || !currentObject.docs.has(d.id))) return false
    if (query && !d.filename.toLocaleLowerCase().includes(query.toLocaleLowerCase())) return false
    if (kind && d.document_type !== kind) return false
    if (receivedSince && !documentReceivedSince(d, receivedSince)) return false
    if (!receivedSince && received !== 'any' && new Date(d.upload_date) < threshold) return false
    if (status === 'attention') return documentNeedsAttention(d, parkedIds)
    if (status === 'unmatched') return parkedIds.has(d.id)
    if (status === 'processing') return PROCESSING.has(d.status)
    if (status === 'processed') return d.status === 'processed' && !parkedIds.has(d.id)
    return status === 'all' || d.status === status
  })
  const shownIds = new Set(visible.map((d) => d.id))
  const parkedVisible = orphans.filter((o) => shownIds.has(o.document_id))
  const backTo = location.pathname + location.search
  const clearFilters = () =>
    changeFilters((next) => {
      for (const key of ['q', 'kind', 'status', 'received', 'received_since', 'object'])
        next.delete(key)
    })

  return (
    <main className="detail traces-flat">
      <div className="dhead">
        <h1>
          Traces <HelpDot k="surface.documents" />
        </h1>
        <FundSelector
          funds={selectorFunds}
          value={fundId}
          onChoose={chooseFund}
          loading={funds === null}
          noun="documents"
        />
      </div>
      <div className="dbody traces-body">
        <div className="traces-views" aria-label="Traces views">
          <button
            className={view === 'documents' ? 'on' : ''}
            aria-pressed={view === 'documents'}
            onClick={() => setFilter('view', '')}
          >
            Documents
          </button>
          <button
            className={view === 'history' ? 'on' : ''}
            aria-pressed={view === 'history'}
            onClick={() => setFilter('view', 'history')}
          >
            Activity / History
          </button>
          {view === 'documents' && canUpload && (
            <button
              className="vbtn primary traces-upload"
              disabled={busy}
              onClick={() => fileInput.current?.click()}
            >
              Upload documents
            </button>
          )}
        </div>
        {fundError ? (
          <ErrorState
            what="funds"
            error={fundError}
            onRetry={load}
            testId="documents-funds-error"
          />
        ) : null}
        {view === 'history' ? (
          <>
            <DailyActivity key={fundId ?? 'all'} fundId={fundId} />
            <RecordHistory fundId={fundId} />
          </>
        ) : (
          <>
            {canUpload && (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  accept="application/pdf"
                  multiple
                  hidden
                  onChange={(e) => {
                    void uploadFiles(Array.from(e.target.files ?? []))
                    e.target.value = ''
                  }}
                />
                <div
                  className={`traces-drop${dragOver ? ' over' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault()
                    setDragOver(true)
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault()
                    setDragOver(false)
                    void uploadFiles(Array.from(e.dataTransfer.files))
                  }}
                >
                  {busy ? (
                    'Uploading…'
                  ) : (
                    <>
                      Drop PDFs here, or{' '}
                      <button onClick={() => fileInput.current?.click()}>choose files</button> ·
                      single files and batches
                    </>
                  )}
                </div>
              </>
            )}
            {outcomes.length > 0 && (
              <div className="traces-outcomes" role="status">
                {outcomes.map((o, i) => (
                  <div key={i}>
                    <strong>{o.name}</strong> · {o.ok ? '✓' : '✕'} {o.detail}
                  </div>
                ))}
              </div>
            )}
            <div className="traces-tools">
              <input
                aria-label="Find a document"
                placeholder="Find a document by name…"
                value={query}
                onChange={(e) => setFilter('q', e.target.value)}
              />
              <select
                aria-label="Document type"
                value={kind}
                onChange={(e) => setFilter('kind', e.target.value)}
              >
                <option value="">All types</option>
                {types.map((t) => (
                  <option key={t} value={t}>
                    {humanizeToken(t)}
                  </option>
                ))}
              </select>
              <select
                aria-label="Document status"
                value={status}
                onChange={(e) => setFilter('status', e.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="attention">Needs attention</option>
                <option value="unmatched">Unmatched to a fund</option>
                <option value="processing">Processing</option>
                <option value="processed">Processed</option>
                <option value="superseded">Superseded</option>
              </select>
              <select
                aria-label="Received date"
                value={received}
                onChange={(e) =>
                  changeFilters((next) => {
                    next.delete('received_since')
                    next.set('received', e.target.value)
                  })
                }
              >
                {receivedSince && (
                  <option value="since">
                    Received since {formatOfficerDateTime(receivedSince)}
                  </option>
                )}
                <option value="any">Received: any date</option>
                <option value="today">Received today</option>
                <option value="7">Received in last 7 days</option>
                <option value="30">Received in last 30 days</option>
              </select>
              {(query || kind || status !== 'all' || received !== 'any') && (
                <button className="traces-clear" onClick={clearFilters}>
                  Clear filters
                </button>
              )}
              {docs !== null && !objectLoading && !currentError && (
                <span className="traces-count" data-testid="documents-count">
                  {visible.length} {visible.length === 1 ? 'document' : 'documents'}
                </span>
              )}
            </div>
            {objectParam && (
              <div className="traces-object" data-testid="documents-object-filter">
                Documents naming: {currentObject?.title ?? 'selected object'}{' '}
                <button onClick={() => setFilter('object', '')}>Clear object filter</button>
              </div>
            )}
            {currentError ? (
              <ErrorState
                what="documents"
                error={currentError}
                onRetry={load}
                testId="documents-error"
              />
            ) : docs === null || objectLoading ? (
              <Skeleton kind="rows" n={6} height={41} />
            ) : (
              <>
                {visible.length === 0 ? (
                  <EmptyState
                    sentence={
                      query || kind || status !== 'all' || received !== 'any' || objectParam
                        ? 'No documents match these filters.'
                        : fundId
                          ? 'No documents for this fund yet.'
                          : 'No documents yet.'
                    }
                    door={
                      query || kind || status !== 'all' || received !== 'any' || objectParam
                        ? { label: 'Clear filters ›', onClick: clearFilters }
                        : canUpload
                          ? { label: 'Choose files ›', onClick: () => fileInput.current?.click() }
                          : { label: 'All funds ›', onClick: () => chooseFund(null) }
                    }
                    testId="documents-empty"
                  />
                ) : (
                  <div className="traces-table-wrap">
                    <table className="traces-table" aria-label="Documents">
                      <colgroup>
                        <col className="doc-name" />
                        <col className="doc-funds" />
                        <col className="doc-type" />
                        <col className="doc-date" />
                        <col className="doc-status" />
                        <col className="doc-pages" />
                        <col className="doc-door" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Document</th>
                          <th>Funds</th>
                          <th>Type</th>
                          <th aria-sort="descending">Received ↓</th>
                          <th>Status</th>
                          <th className="num">Pages</th>
                          <th>
                            <span className="sr-only">Open document</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visible.map((d) => {
                          const parked = parkedIds.has(d.id)
                          const statusText = parked
                            ? 'No fund matched'
                            : d.status === 'processed' && !d.document_type && !d.fund_names.length
                              ? 'Read, nothing recorded'
                              : (STATUS_LABELS[d.status] ?? humanizeToken(d.status))
                          return (
                            <tr key={d.id} data-testid="document-row">
                              <td>
                                <DocumentLink
                                  document={{ document_id: d.id, display_title: d.display_title }}
                                  context={{ returnTo: backTo }}
                                  data-testid="document-row-sentence"
                                />
                                <small className="document-original-name">{d.filename}</small>
                              </td>
                              <td className="traces-funds" title={d.fund_names.join(', ')}>
                                {d.fund_names.length
                                  ? d.fund_names.map((name, i) => (
                                      <span key={`${d.fund_ids?.[i] ?? name}:${i}`}>{name}</span>
                                    ))
                                  : '—'}
                              </td>
                              <td>{d.document_type ? humanizeToken(d.document_type) : '—'}</td>
                              <td className="mono">{formatOfficerDate(d.upload_date)}</td>
                              <td
                                className={
                                  parked || NEEDS_ATTENTION.has(d.status) ? 'traces-attention' : ''
                                }
                              >
                                {statusText}
                                {(d.unlinked_payment_observations ?? 0) > 0 && (
                                  <small className="traces-attention">
                                    {d.unlinked_payment_observations} payment observation
                                    {d.unlinked_payment_observations === 1 ? '' : 's'} not linked
                                  </small>
                                )}
                                {NEEDS_ATTENTION.has(d.status) && d.failure_reason ? (
                                  <small>{failureLabel(d.failure_reason)}</small>
                                ) : null}
                              </td>
                              <td className="num">{d.page_count ?? '—'}</td>
                              <td>
                                <DocumentLink
                                  document={{ document_id: d.id, display_title: d.display_title }}
                                  context={{ returnTo: backTo }}
                                  aria-label={`Open ${d.filename}`}
                                >
                                  Open ›
                                </DocumentLink>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {parkedVisible.length > 0 && (
                  <section className="traces-parked">
                    <h2>No fund matched · {parkedVisible.length}</h2>
                    {parkedVisible.map((o) => (
                      <OrphanLine key={o.document_id} o={o} onRetried={load} />
                    ))}
                  </section>
                )}
                <p className="traces-foot">
                  Received is the upload date. Open a document to see what it supports in the record
                  and the cited pages.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </main>
  )
}

function OrphanLine({ o, onRetried }: { o: OrphanRow; onRetried: () => void }) {
  // No manual force-assign (D4: force_attach stays backend/admin-only) —
  // an orphan resolves by completing the object chain. The officer either
  // adds the missing intermediate object (manual entry, which
  // auto-retries), or — after the chain grew through other documents —
  // hits Retry to re-derive the fund now (POST /api/orphans/{id}/action,
  // the D4-ruled officer surface). A 409 means the chain is still
  // incomplete; the honest error stays on the row.
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function retry() {
    setBusy(true)
    setError(null)
    try {
      await apiFetch(`/api/orphans/${o.document_id}/action`, {
        method: 'POST',
        body: JSON.stringify({ action: 'retry' }),
      })
      onRetried()
    } catch (e) {
      if (e instanceof ApiError) {
        const d = (e.body as { detail?: unknown } | undefined)?.detail
        setError(typeof d === 'string' ? d : `${e.statusText} (${e.status})`)
      } else {
        setError(e instanceof Error ? e.message : 'retry failed')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      data-testid="orphan-row"
      style={{
        padding: '8px 16px',
        borderBottom: '1px solid #f0f2f5',
        fontSize: 'var(--text-12)',
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <LinkedSentence
          className="sn-lead"
          data-testid="orphan-parked-sentence"
          meaning="document.parked"
          values={{ document: o.filename, reason: parkedReason(o.reason) }}
          slot="document"
          href={`/v2/documents/${o.document_id}`}
          documentRef={{ document_id: o.document_id, display_title: o.display_title }}
        />
        <span style={{ marginLeft: 'auto' }} />
        <button
          className="vbtn"
          data-testid="orphan-retry"
          disabled={busy}
          onClick={retry}
          style={{ padding: '4px 12px', fontSize: 'var(--text-12)' }}
          title="Re-derive the fund from the record as it stands now — succeeds once the chain this document needs has been completed"
        >
          Retry
        </button>
        <Link
          className="vbtn"
          to={`/v2/manual?document_id=${encodeURIComponent(o.document_id)}`}
          style={{ padding: '4px 12px', fontSize: 'var(--text-12)', textDecoration: 'none' }}
        >
          Add the missing record item
        </Link>
      </div>
      {error && (
        <div
          data-testid="orphan-retry-error"
          style={{ marginTop: 4, fontSize: 'var(--text-12)', color: 'var(--gap)' }}
        >
          {error}
        </div>
      )}
    </div>
  )
}

// Existing record events, explicitly scoped by the same fund choice as documents.
interface TraceEvent {
  id: string
  event_type: string
  target_type: string
  target_label: string
  recorded_at: string
  actor?: string | null
  actor_kind?: 'person' | 'system' | 'unknown'
  source_type: string
  detail?: string | null
}
function RecordHistory({ fundId }: { fundId: string | null }) {
  const [tick, setTick] = useState(0)
  const [result, setResult] = useState<{
    fundId: string
    events?: TraceEvent[]
    error?: unknown
  } | null>(null)
  useEffect(() => {
    if (!fundId) return
    const controller = new AbortController()
    setResult(null)
    apiFetch<TraceEvent[]>(`/api/funds/${encodeURIComponent(fundId)}/events`, {
      signal: controller.signal,
    })
      .then((events) => {
        if (!controller.signal.aborted) setResult({ fundId, events })
      })
      .catch((error) => {
        if (!controller.signal.aborted) setResult({ fundId, error })
      })
    return () => controller.abort()
  }, [fundId, tick])
  if (!fundId)
    return (
      <EmptyState
        sentence="Choose a fund above to see its record history."
        testId="history-choose-fund"
      />
    )
  const current = result?.fundId === fundId ? result : null
  if (current?.error)
    return (
      <ErrorState
        what="record history"
        error={current.error}
        onRetry={() => setTick((t) => t + 1)}
        testId="history-error"
      />
    )
  if (!current?.events) return <Skeleton kind="rows" n={6} height={41} />
  return (
    <section className="traces-history" data-testid="record-history">
      <h2>Events · audit trail</h2>
      <p>Recorded changes and officer decisions, newest first.</p>
      {current.events.length === 0 ? (
        <EmptyState sentence="No changes recorded yet." testId="history-empty" />
      ) : (
        current.events.map((e) => (
          <div key={e.id} className="traces-history-line" data-testid="history-line">
            <time>{formatOfficerDateTime(e.recorded_at)}</time>
            <div>
              <strong>{humanizeToken(e.event_type.replace(/\./g, ' '))}</strong> ·{' '}
              {humanizeToken(e.target_type)} · {e.target_label}
              {e.detail ? <p>{humanizeTokensIn(cleanEngineText(e.detail))}</p> : null}
              <small>
                {e.actor
                  ? `by ${e.actor}`
                  : e.actor_kind === 'system'
                    ? 'Recorded automatically'
                    : 'Actor not recorded'}{' '}
                · {humanizeToken(e.source_type)}
              </small>
            </div>
          </div>
        ))
      )}
    </section>
  )
}
